import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { FiSearch, FiBox, FiMapPin, FiTool, FiChevronRight, FiChevronDown, FiX, FiPrinter, FiGrid, FiShare2 } from 'react-icons/fi';
import { printBulkQRLabels } from '../components/QRLabelModal';
import { playClick, playSuccess } from '../utils/sounds';

// ────────────────────────────────────────────────────────────────────────────
// Functional-location tree view. Read-only lookup: expand locations, see the
// assets attached to each, and click through to an asset. Search matches FL
// code/name OR asset tag/name — a match auto-expands the chain down to it and
// hides everything off the path so the result is easy to spot.
// ────────────────────────────────────────────────────────────────────────────
function FlTreeView({ flList, authFetch, navigate }) {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(new Set());
  const [showRedundant, setShowRedundant] = useState(false);
  const seededRef = useRef(false);

  useEffect(() => {
    setLoading(true);
    const url = `/api/assets?limit=2000${showRedundant ? '&include_redundant=1' : ''}`;
    authFetch(url)
      .then(r => r.ok ? r.json() : { data: [] })
      .then(d => { setAssets(Array.isArray(d?.data) ? d.data : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [authFetch, showRedundant]);

  // Expand root locations by default so the top of the tree is open.
  useEffect(() => {
    if (seededRef.current || !flList.length) return;
    const roots = flList.filter(f => f.parent_id == null && !f.parent_asset_id).map(f => `f${f.id}`);
    setExpanded(new Set(roots));
    seededRef.current = true;
  }, [flList]);

  // FL hierarchy (parent_id). FLs nested under an asset (parent_asset_id) are
  // grouped separately and rendered under that asset row.
  const childrenByParent = new Map();
  const flsByParentAsset = new Map();
  for (const n of flList) {
    if (n.parent_asset_id) {
      if (!flsByParentAsset.has(n.parent_asset_id)) flsByParentAsset.set(n.parent_asset_id, []);
      flsByParentAsset.get(n.parent_asset_id).push(n);
      continue;
    }
    const k = n.parent_id == null ? 0 : n.parent_id;
    if (!childrenByParent.has(k)) childrenByParent.set(k, []);
    childrenByParent.get(k).push(n);
  }
  const sortFl = arr => arr.sort((a, b) => (a.sort_order - b.sort_order) || a.code.localeCompare(b.code));
  for (const arr of childrenByParent.values()) sortFl(arr);
  for (const arr of flsByParentAsset.values()) sortFl(arr);

  // Group assets: top-level (no parent in set) attach to their FL; children
  // nest under their parent asset.
  const allAssetIds = new Set(assets.map(a => a.id));
  const assetsByFl = new Map();
  const childAssetsByParent = new Map();
  const unassigned = [];
  for (const a of assets) {
    const parentId = a.parent_asset_id || null;
    if (parentId && allAssetIds.has(parentId)) {
      if (!childAssetsByParent.has(parentId)) childAssetsByParent.set(parentId, []);
      childAssetsByParent.get(parentId).push(a);
    } else if (a.functional_location_id) {
      if (!assetsByFl.has(a.functional_location_id)) assetsByFl.set(a.functional_location_id, []);
      assetsByFl.get(a.functional_location_id).push(a);
    } else {
      unassigned.push(a);
    }
  }
  const sortAssets = arr => arr.sort((a, b) => (a.tag || '').localeCompare(b.tag || '') || (a.name || '').localeCompare(b.name || ''));
  for (const arr of assetsByFl.values()) sortAssets(arr);
  for (const arr of childAssetsByParent.values()) sortAssets(arr);
  sortAssets(unassigned);

  // Search: collect matching FLs + assets, then the ancestor chain that must
  // stay visible so the matches are reachable.
  const q = search.trim().toLowerCase();
  const searchActive = !!q;
  const matchedAssetIds = new Set();
  const visibleFlIds = new Set();
  const visibleAssetIds = new Set();
  const searchExpandedFls = new Set();
  const searchExpandedAssets = new Set();
  if (searchActive) {
    const flById = new Map(flList.map(f => [f.id, f]));
    const assetById = new Map(assets.map(a => [a.id, a]));
    const matchedFlIds = new Set();
    for (const f of flList) {
      if ((f.code || '').toLowerCase().includes(q) || (f.name || '').toLowerCase().includes(q)) matchedFlIds.add(f.id);
    }
    for (const a of assets) {
      const hay = `${a.tag || ''} ${a.name || ''} ${a.manufacturer_name || ''} ${a.model_name || ''}`.toLowerCase();
      if (hay.includes(q)) matchedAssetIds.add(a.id);
    }
    const openUpFromFl = (flId) => {
      let cur = flById.get(flId);
      while (cur) {
        visibleFlIds.add(cur.id);
        if (cur.parent_id != null) { searchExpandedFls.add(cur.parent_id); cur = flById.get(cur.parent_id); }
        else if (cur.parent_asset_id) {
          let aid = cur.parent_asset_id;
          while (aid) {
            visibleAssetIds.add(aid); searchExpandedAssets.add(aid);
            const a = assetById.get(aid);
            if (!a) break;
            if (a.parent_asset_id) { aid = a.parent_asset_id; continue; }
            if (a.functional_location_id) { searchExpandedFls.add(a.functional_location_id); cur = flById.get(a.functional_location_id); break; }
            cur = null; break;
          }
          if (!aid) break;
        } else break;
      }
    };
    for (const id of matchedFlIds) openUpFromFl(id);
    for (const aid of matchedAssetIds) {
      visibleAssetIds.add(aid);
      let cur = assetById.get(aid);
      while (cur && cur.parent_asset_id) { visibleAssetIds.add(cur.parent_asset_id); searchExpandedAssets.add(cur.parent_asset_id); cur = assetById.get(cur.parent_asset_id); }
      if (cur && cur.functional_location_id) { searchExpandedFls.add(cur.functional_location_id); openUpFromFl(cur.functional_location_id); }
    }
  }

  const toggle = (id) => setExpanded(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const renderAsset = (a, depth) => {
    if (searchActive && !visibleAssetIds.has(a.id)) return null;
    const kids = (childAssetsByParent.get(a.id) || []);
    const childFls = (flsByParentAsset.get(a.id) || []);
    const visKids = searchActive ? kids.filter(c => visibleAssetIds.has(c.id)) : kids;
    const visChildFls = searchActive ? childFls.filter(f => visibleFlIds.has(f.id)) : childFls;
    const hasChildren = visKids.length > 0 || visChildFls.length > 0;
    const isExp = expanded.has(`a${a.id}`) || searchExpandedAssets.has(a.id);
    const isMatch = matchedAssetIds.has(a.id);
    const isRedundant = Number(a.status_id) === 4;
    return (
      <div key={`a${a.id}`}>
        <div onClick={() => navigate(`/assets/${a.id}`)}
          style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', paddingLeft: 12 + depth * 18, borderBottom: '1px solid var(--border)', cursor: 'pointer', background: isMatch ? 'rgba(250,204,21,0.12)' : 'transparent', borderLeft: '3px solid var(--accent)', opacity: isRedundant ? 0.55 : 1, textDecoration: isRedundant ? 'line-through' : 'none' }}
          onMouseEnter={e => e.currentTarget.style.background = isMatch ? 'rgba(250,204,21,0.2)' : 'rgba(37,99,235,0.05)'}
          onMouseLeave={e => e.currentTarget.style.background = isMatch ? 'rgba(250,204,21,0.12)' : 'transparent'}>
          <button onClick={e => { e.stopPropagation(); if (hasChildren) toggle(`a${a.id}`); }}
            style={{ background: 'none', border: 'none', cursor: hasChildren ? 'pointer' : 'default', color: 'var(--text-muted)', padding: 0, width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            {hasChildren ? (isExp ? <FiChevronDown size={12} /> : <FiChevronRight size={12} />) : <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--text-muted)', opacity: 0.4 }} />}
          </button>
          <div style={{ width: 18, height: 18, borderRadius: 5, background: 'rgba(37,99,235,0.12)', border: '1px solid rgba(37,99,235,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <FiBox size={10} style={{ color: 'var(--accent)' }} />
          </div>
          <span style={{ fontFamily: 'monospace', fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', padding: '1px 6px', borderRadius: 4, background: 'rgba(37,99,235,0.06)', border: '1px solid var(--border)' }}>{a.tag}</span>
          <span style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 500, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {[a.name, a.manufacturer_name, a.model_name].filter(Boolean).join(' ')}
          </span>
          {a.status_name && <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 4, background: `${a.status_color}15`, color: a.status_color, flexShrink: 0 }}>{a.status_name}</span>}
          {a.open_issues > 0 && <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 4, background: 'rgba(217,119,6,0.1)', color: '#d97706', flexShrink: 0 }}><FiTool size={9} style={{ verticalAlign: 'middle' }} /> {a.open_issues}</span>}
          {(visKids.length > 0) && <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 4, background: 'rgba(37,99,235,0.1)', color: 'var(--accent)', flexShrink: 0 }}>+{visKids.length}</span>}
        </div>
        {isExp && visKids.map(c => renderAsset(c, depth + 1))}
        {isExp && visChildFls.length > 0 && (
          <div style={{ marginLeft: 12 + depth * 18 + 30, borderLeft: '2px solid rgba(37,99,235,0.25)', background: 'rgba(37,99,235,0.03)' }}>
            {visChildFls.map(fl => renderFl(fl, 0))}
          </div>
        )}
      </div>
    );
  };

  const renderFl = (node, depth) => {
    if (searchActive && !visibleFlIds.has(node.id)) return null;
    const kids = childrenByParent.get(node.id) || [];
    const allNodeAssets = assetsByFl.get(node.id) || [];
    const nodeAssets = searchActive ? allNodeAssets.filter(a => visibleAssetIds.has(a.id)) : allNodeAssets;
    const hasContent = kids.length > 0 || nodeAssets.length > 0;
    const isExp = expanded.has(`f${node.id}`) || searchExpandedFls.has(node.id);
    return (
      <div key={`f${node.id}`}>
        <div onClick={() => hasContent && toggle(`f${node.id}`)}
          style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', paddingLeft: 12 + depth * 18, borderBottom: '1px solid var(--border)', cursor: hasContent ? 'pointer' : 'default' }}
          onMouseEnter={e => e.currentTarget.style.background = 'rgba(37,99,235,0.04)'}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
          <button style={{ background: 'none', border: 'none', cursor: hasContent ? 'pointer' : 'default', color: 'var(--text-muted)', padding: 0, width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            {hasContent ? (isExp ? <FiChevronDown size={13} /> : <FiChevronRight size={13} />) : <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--text-muted)', opacity: 0.4 }} />}
          </button>
          <FiMapPin size={13} style={{ color: '#8b5cf6', flexShrink: 0 }} />
          <span style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700, color: 'var(--text-primary)', minWidth: 70 }}>{node.code}</span>
          <span style={{ fontSize: 13, color: 'var(--text-primary)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{node.name}</span>
          {nodeAssets.length > 0 && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{nodeAssets.length} asset{nodeAssets.length === 1 ? '' : 's'}</span>}
        </div>
        {isExp && nodeAssets.map(a => renderAsset(a, depth + 1))}
        {isExp && kids.map(c => renderFl(c, depth + 1))}
      </div>
    );
  };

  const roots = childrenByParent.get(0) || [];
  const visibleUnassigned = searchActive ? unassigned.filter(a => visibleAssetIds.has(a.id)) : unassigned;

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 240 }}>
          <FiSearch size={16} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by location or asset name…"
            style={{ width: '100%', padding: '12px 12px 12px 42px', borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
        </div>
        <button onClick={() => setShowRedundant(s => !s)}
          title={showRedundant ? 'Hide redundant assets' : 'Show redundant assets'}
          style={{
            padding: '10px 14px', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer',
            border: `1px solid ${showRedundant ? '#7A7982' : 'var(--border)'}`,
            background: showRedundant ? 'rgba(122,121,130,0.12)' : 'var(--bg-card)',
            color: showRedundant ? '#7A7982' : 'var(--text-secondary)',
            whiteSpace: 'nowrap',
          }}>
          {showRedundant ? 'Showing redundant' : 'Show redundant'}
        </button>
      </div>
      {loading ? (
        <div style={{ padding: 40 }}>{[...Array(5)].map((_, i) => <div key={i} className="skeleton" style={{ height: 38, borderRadius: 8, marginBottom: 8 }} />)}</div>
      ) : (
        <div style={{ background: 'var(--bg-card)', borderRadius: 12, border: '1px solid var(--border)', overflow: 'hidden' }}>
          {roots.length === 0 && visibleUnassigned.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
              <FiMapPin size={40} style={{ marginBottom: 12, opacity: 0.4 }} />
              <p style={{ fontSize: 15, fontWeight: 600 }}>{searchActive ? 'No matching locations or assets' : 'No functional locations yet'}</p>
              {!searchActive && <p style={{ fontSize: 13 }}>Build the location tree in Admin → Functional Locations.</p>}
            </div>
          ) : (
            <>
              {roots.map(n => renderFl(n, 0))}
              {visibleUnassigned.length > 0 && (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', borderBottom: '1px solid var(--border)', background: 'rgba(148,163,184,0.06)' }}>
                    <FiBox size={13} style={{ color: 'var(--text-muted)' }} />
                    <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)' }}>Unassigned</span>
                    <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{visibleUnassigned.length} asset{visibleUnassigned.length === 1 ? '' : 's'} with no location</span>
                  </div>
                  {visibleUnassigned.map(a => renderAsset(a, 1))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function Assets() {
  const { authFetch } = useAuth();
  const navigate = useNavigate();
  const [assets, setAssets] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [statuses, setStatuses] = useState([]);
  const [lookups, setLookups] = useState({});
  const [selectedAssets, setSelectedAssets] = useState(new Set());
  const [selectMode, setSelectMode] = useState(false);
  const [searching, setSearching] = useState(false);
  // Inline FL-edit popover state — click an asset row's FL chip to open it.
  const [flPickerAssetId, setFlPickerAssetId] = useState(null);
  const [flList, setFlList] = useState([]);
  const [flPickerSearch, setFlPickerSearch] = useState('');
  const [flPickerSaving, setFlPickerSaving] = useState(false);
  // 'Top-level only' filter hides child assets — top-level only by default
  // so sub-asset rows don't repeat under the parent.
  const [topLevelOnly, setTopLevelOnly] = useState(true);
  // Default to the functional-location tree view; 'cards' is the old
  // category/grid browse, kept behind a toggle (QR labels, category cards).
  const [viewMode, setViewMode] = useState('tree');
  const LIMIT = 50;

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => { setSearchDebounced(search); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const loadAssets = () => {
    if (assets.length === 0) setLoading(true);
    setSearching(true);
    const params = new URLSearchParams({ page, limit: LIMIT });
    if (statusFilter !== 'All') params.set('status', statusFilter);
    if (searchDebounced.trim()) params.set('search', searchDebounced.trim());
    if (topLevelOnly) params.set('top_level_only', 'true');
    authFetch(`/api/assets?${params.toString()}`)
      .then(r => { if (!r.ok) throw new Error('Failed'); return r.json(); })
      .then(data => {
        if (data && data.data) {
          setAssets(data.data);
          setTotal(data.total);
          setTotalPages(data.totalPages);
        } else {
          const arr = Array.isArray(data) ? data : [];
          setAssets(arr);
        }
        setLoading(false); setSearching(false);
      })
      .catch(() => { setAssets([]); setLoading(false); setSearching(false); });
  };

  useEffect(() => {
    loadAssets();
  }, [page, statusFilter, searchDebounced, topLevelOnly]);

  useEffect(() => {
    authFetch('/api/assets/lookups').then(r => r.ok ? r.json() : {}).then(data => {
      setLookups(data);
      if (data.statuses) setStatuses(data.statuses.map(s => s.name));
      if (Array.isArray(data.functionalLocations)) setFlList(data.functionalLocations);
    }).catch(() => {});
  }, []);

  // Quick-edit FL helpers
  const flByParent = (() => {
    const m = new Map();
    for (const n of flList) {
      const k = n.parent_id == null ? 0 : n.parent_id;
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(n);
    }
    for (const arr of m.values()) arr.sort((a, b) => (a.sort_order - b.sort_order) || a.code.localeCompare(b.code));
    return m;
  })();
  // Flat, depth-aware list for the picker. Containers (have children) are
  // shown but disabled — assets attach to leaves only.
  const flFlat = (() => {
    const out = [];
    const walk = (parentId, depth) => {
      for (const n of (flByParent.get(parentId) || [])) {
        const hasKids = (flByParent.get(n.id) || []).length > 0;
        out.push({ ...n, depth, hasKids });
        walk(n.id, depth + 1);
      }
    };
    walk(0, 0);
    return out;
  })();
  const filteredFlOptions = (() => {
    const q = flPickerSearch.trim().toLowerCase();
    if (!q) return flFlat;
    return flFlat.filter(n => n.code.toLowerCase().includes(q) || (n.name || '').toLowerCase().includes(q));
  })();
  const handleAssignFl = async (assetId, flId) => {
    setFlPickerSaving(true);
    try {
      const res = await authFetch('/api/assets/bulk-assign-fl', {
        method: 'POST',
        body: JSON.stringify({ asset_ids: [assetId], functional_location_id: flId })
      });
      if (res.ok) {
        playSuccess();
        // Optimistic update: patch the row locally then reload to refresh
        // counts and the FL name/code.
        setAssets(prev => prev.map(a => a.id === assetId ? {
          ...a,
          functional_location_id: flId,
          functional_location_code: flId ? (flList.find(n => n.id === flId)?.code) : null,
          functional_location_name: flId ? (flList.find(n => n.id === flId)?.name) : null,
        } : a));
        setFlPickerAssetId(null);
        setFlPickerSearch('');
      }
    } finally {
      setFlPickerSaving(false);
    }
  };

  const filtered = assets;

  if (loading && viewMode !== 'tree') return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <div className="skeleton" style={{ width: 120, height: 28, marginBottom: 8 }} />
        <div className="skeleton" style={{ width: 160, height: 14 }} />
      </div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
        <div className="skeleton" style={{ flex: 1, height: 46, borderRadius: 10 }} />
        <div className="skeleton" style={{ width: 150, height: 46, borderRadius: 10 }} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 320px), 1fr))', gap: 16 }}>
        {[...Array(8)].map((_, i) => (
          <div key={i} style={{ background: 'var(--bg-card)', borderRadius: 16, padding: '20px', border: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <div className="skeleton" style={{ width: 50, height: 20, borderRadius: 6 }} />
              <div className="skeleton" style={{ width: 60, height: 20, borderRadius: 6 }} />
            </div>
            <div className="skeleton" style={{ width: '80%', height: 18, marginBottom: 12 }} />
            <div className="skeleton" style={{ width: '60%', height: 14, marginBottom: 6 }} />
            <div className="skeleton" style={{ width: '50%', height: 14 }} />
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div>
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
          <h1 style={{ fontSize: 'clamp(20px, 5vw, 28px)', fontWeight: 800, letterSpacing: '-0.5px', color: 'var(--text-primary)', margin: 0 }}>
            Assets
          </h1>
          {/* View toggle: tree (default) ↔ flat grid */}
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 0, borderRadius: 9, overflow: 'hidden', border: '1px solid var(--border)' }}>
            <button onClick={() => { playClick(); setViewMode('tree'); }}
              title="Browse by functional location"
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 700, background: viewMode === 'tree' ? 'var(--accent)' : 'var(--bg-card)', color: viewMode === 'tree' ? '#fff' : 'var(--text-secondary)' }}>
              <FiShare2 size={14} /> Locations
            </button>
            <button onClick={() => { playClick(); setViewMode('cards'); }}
              title="Flat grid of all assets"
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px', border: 'none', borderLeft: '1px solid var(--border)', cursor: 'pointer', fontSize: 12, fontWeight: 700, background: viewMode === 'cards' ? 'var(--accent)' : 'var(--bg-card)', color: viewMode === 'cards' ? '#fff' : 'var(--text-secondary)' }}>
              <FiGrid size={14} /> Grid
            </button>
          </div>
        </div>
        <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginBottom: 20 }}>
          {viewMode === 'tree'
            ? 'Browse assets by functional location — expand a location or search by location or asset name.'
            : `${total} total assets`}
        </p>
      </motion.div>

      {viewMode === 'tree' && <FlTreeView flList={flList} authFetch={authFetch} navigate={navigate} />}

      {/* Search bar (grid view) */}
      {viewMode === 'cards' && (
        <div style={{ position: 'relative', marginBottom: 20 }}>
          <FiSearch size={16} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search all assets..."
            style={{ width: '100%', padding: '12px 12px 12px 42px', borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
          />
          {searching && search && <span style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', fontSize: 12, color: 'var(--text-muted)' }}>Searching...</span>}
        </div>
      )}

      {/* Filters (grid view) */}
      {viewMode === 'cards' && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 24 }}
        >
          <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
            style={{ padding: '12px 16px', borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: 13, outline: 'none', cursor: 'pointer' }}>
            <option value="All">All Statuses</option>
            {statuses.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <motion.button whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
            onClick={() => { setTopLevelOnly(t => !t); setPage(1); }}
            title={topLevelOnly ? 'Currently hiding sub-assets. Click to show every asset.' : 'Currently showing every asset, including sub-assets. Click to hide children.'}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 14px', borderRadius: 10, border: `1px solid ${topLevelOnly ? 'rgba(139,92,246,0.5)' : 'var(--border)'}`, background: topLevelOnly ? 'rgba(139,92,246,0.08)' : 'var(--bg-card)', color: topLevelOnly ? '#8b5cf6' : 'var(--text-secondary)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            <FiBox size={14} /> {topLevelOnly ? 'Top-level only' : 'Show all (incl. children)'}
          </motion.button>
          <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
            onClick={() => { setSelectMode(!selectMode); setSelectedAssets(new Set()); }}
            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', borderRadius: 10, border: `1px solid ${selectMode ? 'var(--accent)' : 'var(--border)'}`, background: selectMode ? 'rgba(37,99,235,0.08)' : 'var(--bg-card)', color: selectMode ? 'var(--accent)' : 'var(--text-secondary)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            <FiPrinter size={16} /> {selectMode ? 'Cancel' : 'QR Labels'}
          </motion.button>
          {selectMode && selectedAssets.size > 0 && (
            <motion.button initial={{ scale: 0.9 }} animate={{ scale: 1 }} whileTap={{ scale: 0.95 }}
              onClick={() => { playClick(); printBulkQRLabels(filtered.filter(a => selectedAssets.has(a.id))); }}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', borderRadius: 10, border: 'none', background: '#10b981', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              <FiPrinter size={16} /> Print {selectedAssets.size} Label{selectedAssets.size !== 1 ? 's' : ''}
            </motion.button>
          )}
        </motion.div>
      )}

      {viewMode === 'cards' && <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 320px), 1fr))', gap: 16 }}>
        <AnimatePresence>
          {filtered.map((asset, idx) => (
            <motion.div
              key={asset.id} layout
              initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }}
              transition={{ delay: Math.min(idx * 0.03, 0.5) }}
              whileHover={{ scale: 1.02, y: -4 }} whileTap={{ scale: 0.98 }}
              onClick={() => {
                playClick();
                if (selectMode) {
                  setSelectedAssets(prev => {
                    const next = new Set(prev);
                    if (next.has(asset.id)) next.delete(asset.id); else next.add(asset.id);
                    return next;
                  });
                } else {
                  navigate(`/assets/${asset.id}`);
                }
              }}
              style={{ background: selectedAssets.has(asset.id) ? 'rgba(37,99,235,0.04)' : 'var(--bg-card)', borderRadius: 16, padding: '20px', border: `1px solid ${selectedAssets.has(asset.id) ? 'var(--accent)' : 'var(--border)'}`, cursor: 'pointer', position: 'relative', overflow: 'hidden', transition: 'all 0.15s', boxShadow: 'var(--shadow)' }}
              onMouseEnter={e => { if (!selectMode) { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.background = 'rgba(37,99,235,0.04)'; } }}
              onMouseLeave={e => { if (!selectedAssets.has(asset.id)) { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'var(--bg-card)'; } }}
            >
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: 'var(--accent)' }} />
              {selectMode && (
                <div style={{ position: 'absolute', top: 10, right: 10, width: 22, height: 22, borderRadius: 6, border: `2px solid ${selectedAssets.has(asset.id) ? 'var(--accent)' : 'var(--border)'}`, background: selectedAssets.has(asset.id) ? 'var(--accent)' : 'var(--bg-card)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1 }}>
                  {selectedAssets.has(asset.id) && <span style={{ color: '#fff', fontSize: 14, fontWeight: 700 }}>&#10003;</span>}
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600, background: 'rgba(37,99,235,0.08)', color: 'var(--accent)' }}>{asset.tag}</span>
                    {asset.status_name && (
                      <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600, background: `${asset.status_color}15`, color: asset.status_color }}>{asset.status_name}</span>
                    )}
                  </div>
                  <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 4, lineHeight: 1.3, color: 'var(--text-primary)' }}>{asset.name}</h3>
                </div>
                <FiChevronRight size={18} color="var(--text-muted)" />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {asset.manufacturer_name && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--text-secondary)' }}>
                    <FiBox size={13} style={{ flexShrink: 0 }} />
                    <span>{asset.manufacturer_name}{asset.model_name ? ` - ${asset.model_name}` : ''}</span>
                  </div>
                )}
                {flList.length > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--text-secondary)', position: 'relative' }}>
                    {/* Clickable chip / 'set FL' button. Click opens the
                        inline picker popover for this asset. */}
                    <button
                      onClick={e => { e.stopPropagation(); setFlPickerAssetId(asset.id === flPickerAssetId ? null : asset.id); setFlPickerSearch(''); }}
                      title={asset.functional_location_code ? 'Click to change functional location' : 'Click to assign a functional location'}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        padding: '2px 8px', borderRadius: 4, cursor: 'pointer',
                        background: asset.functional_location_code ? 'rgba(139,92,246,0.10)' : 'rgba(148,163,184,0.10)',
                        border: `1px dashed ${asset.functional_location_code ? 'rgba(139,92,246,0.4)' : 'var(--border)'}`,
                        color: asset.functional_location_code ? '#8b5cf6' : 'var(--text-muted)',
                        fontFamily: asset.functional_location_code ? 'monospace' : 'inherit',
                        fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap'
                      }}>
                      {asset.functional_location_code || '+ FL'}
                    </button>
                    {asset.functional_location_name && (
                      <span style={{ fontSize: 11, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{asset.functional_location_name}</span>
                    )}
                    {flPickerAssetId === asset.id && (
                      <>
                        <div onClick={e => { e.stopPropagation(); setFlPickerAssetId(null); }}
                          style={{ position: 'fixed', inset: 0, zIndex: 90 }} />
                        <div onClick={e => e.stopPropagation()}
                          style={{
                            position: 'absolute', top: '100%', left: 0, marginTop: 6, zIndex: 95,
                            width: 320, maxHeight: 360, background: 'var(--bg-card)',
                            border: '1px solid var(--border)', borderRadius: 10, boxShadow: '0 12px 32px rgba(0,0,0,0.25)',
                            display: 'flex', flexDirection: 'column', overflow: 'hidden',
                          }}>
                          <div style={{ padding: 8, borderBottom: '1px solid var(--border)' }}>
                            <input autoFocus value={flPickerSearch} onChange={e => setFlPickerSearch(e.target.value)}
                              placeholder="Search FL by code or name…"
                              style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 12, outline: 'none', boxSizing: 'border-box' }} />
                          </div>
                          <div style={{ flex: 1, overflowY: 'auto' }}>
                            {asset.functional_location_id != null && (
                              <button onClick={() => handleAssignFl(asset.id, null)} disabled={flPickerSaving}
                                style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px', background: 'transparent', border: 'none', borderBottom: '1px solid var(--border)', color: '#dc2626', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                                <FiX size={11} style={{ verticalAlign: 'middle', marginRight: 6 }} /> Unassign (clear FL)
                              </button>
                            )}
                            {filteredFlOptions.length === 0 ? (
                              <div style={{ padding: 14, textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>No matches</div>
                            ) : filteredFlOptions.map(n => (
                              <button key={n.id} onClick={() => !n.hasKids && handleAssignFl(asset.id, n.id)}
                                disabled={n.hasKids || flPickerSaving || n.id === asset.functional_location_id}
                                style={{
                                  display: 'block', width: '100%', textAlign: 'left',
                                  padding: '6px 12px', paddingLeft: 12 + n.depth * 14,
                                  background: n.id === asset.functional_location_id ? 'rgba(139,92,246,0.10)' : 'transparent',
                                  border: 'none', borderBottom: '1px solid var(--border)',
                                  color: n.hasKids ? 'var(--text-muted)' : 'var(--text-primary)',
                                  cursor: n.hasKids ? 'not-allowed' : 'pointer',
                                  fontSize: 12, fontFamily: 'monospace',
                                  opacity: n.hasKids ? 0.6 : 1,
                                }}
                                onMouseEnter={e => { if (!n.hasKids) e.currentTarget.style.background = 'rgba(139,92,246,0.06)'; }}
                                onMouseLeave={e => { if (n.id !== asset.functional_location_id) e.currentTarget.style.background = 'transparent'; }}>
                                <span style={{ fontWeight: 700, color: n.hasKids ? 'var(--text-muted)' : '#8b5cf6' }}>{n.code}</span>
                                <span style={{ fontFamily: 'inherit', marginLeft: 8, color: 'var(--text-secondary)' }}>{n.name}</span>
                                {n.hasKids && <span style={{ fontFamily: 'inherit', marginLeft: 6, fontSize: 10 }}>(container)</span>}
                              </button>
                            ))}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}
                {asset.serial_number && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--text-muted)' }}>
                    <span style={{ fontFamily: 'monospace', fontSize: 11 }}>SN: {asset.serial_number}</span>
                  </div>
                )}
                {/* Sub-asset chip — clicking jumps to the parent asset */}
                {asset.parent_asset_id && asset.parent_asset_tag && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--text-muted)' }}>
                    <span>↳ child of</span>
                    <button onClick={e => { e.stopPropagation(); navigate(`/assets/${asset.parent_asset_id}`); }}
                      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, color: '#8b5cf6', fontSize: 11, fontWeight: 700 }}>
                      <span style={{ fontFamily: 'monospace' }}>{asset.parent_asset_tag}</span>
                      <span style={{ color: 'var(--text-secondary)', fontWeight: 500 }}>— {asset.parent_asset_name}</span>
                    </button>
                  </div>
                )}
                {/* '+N sub-assets' badge when this asset has its own children */}
                {asset.child_asset_count > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--text-muted)' }}>
                    <span>↳ has</span>
                    <span style={{ fontWeight: 700, color: '#8b5cf6' }}>{asset.child_asset_count}</span>
                    <span>sub-asset{asset.child_asset_count === 1 ? '' : 's'}</span>
                  </div>
                )}
              </div>
              {asset.issue_count > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 12, padding: '6px 10px', borderRadius: 8, fontSize: 11, fontWeight: 600, background: asset.open_issues > 0 ? 'rgba(217,119,6,0.08)' : 'rgba(22,163,74,0.08)', color: asset.open_issues > 0 ? '#d97706' : '#16a34a' }}>
                  <FiTool size={12} />
                  {asset.issue_count} work order{asset.issue_count !== 1 ? 's' : ''}
                  {asset.open_issues > 0 && ` (${asset.open_issues} open)`}
                </div>
              )}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>}

      {viewMode === 'cards' && filtered.length === 0 && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-muted)' }}>
          <FiSearch size={48} style={{ marginBottom: 16, opacity: 0.5 }} />
          <p style={{ fontSize: 16, fontWeight: 500 }}>No assets found</p>
          <p style={{ fontSize: 13 }}>Try adjusting your search or filters</p>
        </motion.div>
      )}

      {/* Pagination */}
      {viewMode === 'cards' && totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, marginTop: 20 }}>
          <button onClick={() => setPage(1)} disabled={page === 1}
            style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', cursor: page === 1 ? 'default' : 'pointer', opacity: page === 1 ? 0.4 : 1, fontSize: 12, fontWeight: 600 }}>First</button>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
            style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', cursor: page === 1 ? 'default' : 'pointer', opacity: page === 1 ? 0.4 : 1, fontSize: 12, fontWeight: 600 }}>Prev</button>
          <span style={{ fontSize: 13, color: 'var(--text-secondary)', fontWeight: 500 }}>
            Page {page} of {totalPages}
          </span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
            style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', cursor: page === totalPages ? 'default' : 'pointer', opacity: page === totalPages ? 0.4 : 1, fontSize: 12, fontWeight: 600 }}>Next</button>
          <button onClick={() => setPage(totalPages)} disabled={page === totalPages}
            style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', cursor: page === totalPages ? 'default' : 'pointer', opacity: page === totalPages ? 0.4 : 1, fontSize: 12, fontWeight: 600 }}>Last</button>
        </div>
      )}
    </div>
  );
}
